import React from 'react';
import { RightOutlined, DownOutlined, EyeOutlined, PushpinOutlined } from '@ant-design/icons';
import { CardSectionProps } from '../../../types/compositeCards';

interface CardHeaderProps extends CardSectionProps {
  onCollapse?: (collapsed: boolean) => void;
  onSelect?: () => void;
  onPinToggle?: (pinned: boolean) => void;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  section,
  card,
  onAction,
  onCollapse,
  onSelect,
  onPinToggle
}) => {
  const { layout, data, context, isCollapsed = false, isPinned = false } = card;
  const { uiRole, modelType } = context;

  const handlePinToggle = () => {
    onPinToggle?.(!isPinned);
  };

  // Get artifact information based on card type
  const getArtifactInfo = () => {
    if ('artifact' in data) {
      return {
        name: data.artifact.name,
        type: data.artifact.type,
        icon: getArtifactIcon(data.artifact.type),
        color: getArtifactColor(data.artifact.type)
      };
    }
    if ('process' in data) {
      return {
        name: data.process.name,
        type: 'process',
        icon: 'PR',
        color: '#3b82f6'
      };
    }
    if ('items' in data && Array.isArray(data.items)) {
      const itemCount = data.items.length;
      const itemType = data.itemType || 'item';
      return {
        name: `${itemCount} ${itemType}${itemCount !== 1 ? 's' : ''}`,
        type: 'list',
        icon: 'LS',
        color: '#6b7280'
      };
    }
    return {
      name: 'Unknown',
      type: 'unknown',
      icon: '?',
      color: '#6b7280'
    };
  };

  const getArtifactIcon = (artifactType: string) => {
    switch (artifactType) {
      case 'process': return 'PR';
      case 'business_entity': return 'BE';
      case 'solution': return 'SO';
      case 'situation': return 'SI';
      case 'improvement': return 'IM';
      default: return artifactType?.slice(0, 2).toUpperCase() || 'AR';
    }
  };

  const getArtifactColor = (artifactType: string) => {
    switch (artifactType) {
      case 'process': return '#3b82f6';
      case 'business_entity': return '#00d455';
      case 'solution': return '#8b5cf6';
      case 'situation': return '#f59e0b';
      case 'improvement': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const artifactInfo = getArtifactInfo();

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'space-between',
      marginBottom: '12px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
        {/* Collapse Button */}
        <button
          onClick={() => onCollapse?.(!isCollapsed)}
          style={{
            padding: '4px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
          }}
          title={isCollapsed ? 'Expand card' : 'Collapse card'}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          {isCollapsed ? 
            <RightOutlined style={{ fontSize: '14px', color: '#6b7280' }} /> : 
            <DownOutlined style={{ fontSize: '14px', color: '#6b7280' }} />
          }
        </button>

        {/* Type Icon */}
        <div style={{
          height: '32px',
          width: '32px',
          backgroundColor: artifactInfo.color,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: '500',
          color: 'white',
        }}>
          {artifactInfo.icon}
        </div>

        {/* Title and Subtitle */}
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: '500', margin: 0, color: '#1f2937' }}>
            {artifactInfo.name}
          </h3>
          <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
            {artifactInfo.type} • {uiRole} view • {modelType}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '8px' }}>
        {/* Pin Button */}
        <button
          style={{
            padding: '8px',
            backgroundColor: isPinned ? '#f59e0b' : '#f3f4f6',
            color: isPinned ? 'white' : '#6b7280',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handlePinToggle}
          title={isPinned ? 'Unpin card' : 'Pin card'}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = isPinned ? '#d97706' : '#e5e7eb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = isPinned ? '#f59e0b' : '#f3f4f6';
          }}
        >
          <PushpinOutlined style={{ fontSize: '16px' }} />
        </button>

        {/* Select Button */}
        <button
          style={{
            padding: '8px',
            backgroundColor: artifactInfo.color,
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => onSelect?.()}
          title="Show in context"
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = artifactInfo.color + 'dd'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = artifactInfo.color}
        >
          <EyeOutlined style={{ fontSize: '16px' }} />
        </button>
      </div>
    </div>
  );
};
